import React, { useState, useMemo, useEffect } from 'react';
import { X, Trophy, Plus, AlertCircle, RotateCcw } from 'lucide-react';
import { useProfile } from '../../context/ProfileContext';
import MediaCaptureMenu from '../MediaCaptureMenu';
import PasteUrlInput from '../PasteUrlInput';
import AttachmentTile from '../AttachmentTile';
import { fileToPayload, urlToPayload } from '../../lib/mediaCapture';
import MultiSelectPills from './MultiSelectPills';
import ChipInput from './ChipInput';
import MTTechniqueRow from './MTTechniqueRow';
import {
  MT_CLASS_FORMATS,
  MT_FOCUS_MODES,
  MT_SPARRING_TRIGGERS,
  MT_FELT_SCALE,
  newMTTechniqueRow,
  newMTSparring,
  newMTReflection,
  newMTInjury,
} from '../../data/combativesMT';

const DRAFT_KEY = 'enferno_mt_session_draft_v1';

const formatTimeAgo = (iso) => {
  if (!iso) return 'earlier';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'a moment ago';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * MuayThaiLogDialog — rich session journal for Muay Thai. Routed in
 * Training.jsx when art.id === 'muay-thai'. Cloned from BJJLogDialog
 *  with Muay Thai vocabulary substituted:
 *   - position taxonomy → range/type pickers (striking, not grappling)
 *   - submissions/sweeps/passes → knockdowns landed/absorbed
 *   - no weapons toggle, no submission field, no step editor
 *
 * Same architecture for autosave, dirty close, draft restore, edit-mode
 * prefill, autocomplete sourcing.
 */
const MuayThaiLogDialog = ({ art, onClose, onSave, initialData = null }) => {
  const { profile, training } = useProfile();
  const beltSnapshot = (profile?.ranks?.['muay-thai']) || null;
  const isEditMode = !!initialData;

  // Pull autocomplete sources from this art's session history.
  const autocompleteSuggestions = useMemo(() => {
    const techniques = new Set();
    const coaches = new Set();
    const partners = new Set();
    (training?.sessions || [])
      .filter((s) => s.artId === 'muay-thai')
      .forEach((s) => {
        if (initialData && s.id === initialData.id) return;
        (s.techniques || []).forEach((t) => { if (t?.name) techniques.add(t.name); });
        (s.coaches || []).forEach((c) => { if (c) coaches.add(c); });
        (s.sparring?.partners || []).forEach((p) => { if (p) partners.add(p); });
      });
    return {
      techniques: Array.from(techniques).sort(),
      coaches: Array.from(coaches).sort(),
      partners: Array.from(partners).sort(),
    };
  }, [training?.sessions, initialData]);

  const todayLocal = useMemo(() => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  }, []);
  const minLocal = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  }, []);

  const buildInitialForm = () => {
    if (!initialData) {
      return {
        date: todayLocal,
        duration: '60',
        classFormat: [],
        focusModes: [],
        coaches: [],
        intent: '',
        techniques: [],
        sparring: newMTSparring(),
        reflection: newMTReflection(),
        injuryEnabled: false,
        injury: newMTInjury(),
        notes: '',
        attachments: [],
      };
    }
    const d = initialData.date ? new Date(initialData.date) : new Date();
    const dateStr = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    const numToStr = (n) => (n === 0 || n == null) ? '' : String(n);
    const sp = initialData.sparring;
    return {
      date: dateStr,
      duration: String(initialData.duration ?? '60'),
      classFormat: initialData.classFormat || [],
      focusModes: initialData.focusModes || [],
      coaches: initialData.coaches || [],
      intent: initialData.intent || '',
      techniques: initialData.techniques || [],
      sparring: sp ? {
        rounds: numToStr(sp.rounds),
        partners: sp.partners || [],
        feltScale: sp.feltScale ?? 3,
        knockdownsLanded: numToStr(sp.knockdownsLanded),
        knockdownsAbsorbed: numToStr(sp.knockdownsAbsorbed),
        note: sp.note || '',
      } : newMTSparring(),
      reflection: initialData.reflection || newMTReflection(),
      injuryEnabled: !!initialData.injury,
      injury: initialData.injury || newMTInjury(),
      notes: initialData.notes || '',
      attachments: initialData.attachments || [],
    };
  };

  const [form, setFormState] = useState(buildInitialForm);
  const [error, setError] = useState('');
  const [restoredFromDraft, setRestoredFromDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const setForm = (next) => {
    setDirty(true);
    setFormState((prev) => {
      const updated = typeof next === 'function' ? next(prev) : next;
      if (!isEditMode) {
        try {
          localStorage.setItem(DRAFT_KEY, JSON.stringify({ savedAt: new Date().toISOString(), form: updated }));
        } catch { /* fail silent */ }
      }
      return updated;
    });
  };

  useEffect(() => {
    if (isEditMode) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft?.form) {
        setFormState(draft.form);
        setRestoredFromDraft(draft.savedAt || null);
        setDirty(true);
      }
    } catch { /* corrupt — ignore */ }
  }, [isEditMode]);

  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    setRestoredFromDraft(null);
  };
  const startOver = () => {
    clearDraft();
    setFormState(buildInitialForm());
    setDirty(false);
  };
  const handleClose = () => {
    if (!dirty) { onClose(); return; }
    setConfirmDiscard(true);
  };
  const confirmDiscardYes = () => {
    if (!isEditMode) clearDraft();
    setConfirmDiscard(false);
    onClose();
  };
  const confirmDiscardNo = () => setConfirmDiscard(false);

  const showSparring = form.focusModes.some((m) => MT_SPARRING_TRIGGERS.includes(m));

  const updateTechnique = (idx, next) => setForm((prev) => ({
    ...prev, techniques: prev.techniques.map((t, i) => (i === idx ? next : t)),
  }));
  const removeTechnique = (idx) => setForm((prev) => ({ ...prev, techniques: prev.techniques.filter((_, i) => i !== idx) }));
  const addTechnique = () => setForm((prev) => ({ ...prev, techniques: [newMTTechniqueRow(), ...prev.techniques] }));

  const updSparring = (patch) => setForm((p) => ({ ...p, sparring: { ...p.sparring, ...patch } }));
  const updReflection = (patch) => setForm((p) => ({ ...p, reflection: { ...p.reflection, ...patch } }));
  const updInjury = (patch) => setForm((p) => ({ ...p, injury: { ...p.injury, ...patch } }));

  const addAttachment = (payload) => {
    setError('');
    if (!payload) return;
    if (payload instanceof Blob && !('kind' in payload)) {
      fileToPayload(payload).then((p) => setForm((prev) => ({ ...prev, attachments: [...prev.attachments, p] })));
      return;
    }
    setForm((prev) => ({ ...prev, attachments: [...prev.attachments, payload] }));
  };
  const addExternalUrl = (url) => setForm((prev) => ({ ...prev, attachments: [...prev.attachments, urlToPayload(url)] }));
  const removeAtt = (i) => setForm({ ...form, attachments: form.attachments.filter((_, idx) => idx !== i) });

  const handleSave = () => {
    const isToday = form.date === todayLocal;
    const sessionDate = isToday ? new Date().toISOString() : new Date(`${form.date}T12:00:00`).toISOString();
    const sparringPayload = showSparring ? {
      rounds: Number(form.sparring.rounds) || 0,
      partners: form.sparring.partners,
      feltScale: form.sparring.feltScale,
      knockdownsLanded: Number(form.sparring.knockdownsLanded) || 0,
      knockdownsAbsorbed: Number(form.sparring.knockdownsAbsorbed) || 0,
      note: form.sparring.note,
    } : null;
    onSave({
      date: sessionDate,
      tz_offset_minutes: new Date().getTimezoneOffset(),
      duration: Number(form.duration) || 60,
      focus: form.focusModes[0] || 'Drilling',
      notes: form.notes,
      attachments: form.attachments,
      classFormat: form.classFormat,
      focusModes: form.focusModes,
      coaches: form.coaches,
      beltSnapshot,
      intent: form.intent,
      techniques: form.techniques,
      sparring: sparringPayload,
      reflection: form.reflection,
      injury: form.injuryEnabled ? form.injury : null,
    });
    if (!isEditMode) clearDraft();
  };

  const sectionLabel = 'text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block';
  const sectionHeading = 'text-[11px] uppercase tracking-[0.25em] text-enf-role-info font-semibold mb-3';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={handleClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6" data-testid="mt-log-dialog">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-enf-role-info font-semibold mb-1">{isEditMode ? 'Edit session' : 'Log session'} &middot; {art.shortName}</div>
            <div className="font-display text-3xl tracking-wide leading-none">Combatives journal</div>
          </div>
          <button onClick={handleClose} aria-label="Close" className="text-enf-text-muted hover:text-enf-text-primary"><X className="w-5 h-5" /></button>
        </div>

        {restoredFromDraft && (
          <div className="mb-5 p-3 rounded-md bg-enf-role-info/10 border border-enf-role-info/30 flex items-center justify-between gap-3" data-testid="mt-draft-restored-banner">
            <div className="flex items-center gap-2 min-w-0">
              <RotateCcw className="w-4 h-4 text-enf-role-info flex-shrink-0" />
              <span className="text-xs text-enf-text-secondary truncate">Restored unsaved draft from {formatTimeAgo(restoredFromDraft)}</span>
            </div>
            <button type="button" onClick={startOver} data-testid="mt-draft-start-over" className="text-xs font-semibold text-enf-role-warning hover:text-enf-text-primary flex-shrink-0">Start over</button>
          </div>
        )}

        {/* Intent */}
        <div className="mb-6 p-4 rounded-lg bg-enf-accent/10 border border-enf-accent-fg/40">
          <label className="text-[11px] uppercase tracking-[0.25em] text-enf-accent-fg font-semibold mb-2 block">Pre-session intent &middot; what are you working today?</label>
          <input type="text" value={form.intent} onChange={(e) => setForm({ ...form, intent: e.target.value })}
            placeholder="e.g. Teep timing against southpaw stance"
            data-testid="mt-intent"
            className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-accent-fg/30 rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg" />
        </div>

        {/* Header */}
        <div className="mb-6 space-y-4">
          <div className={sectionHeading}>Session</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={sectionLabel}>Date</label>
              <input type="date" value={form.date} max={todayLocal} min={minLocal}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="mt-date"
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg" />
            </div>
            <div>
              <label className={sectionLabel}>Duration (min)</label>
              <input type="number" inputMode="numeric" value={form.duration}
                onChange={(e) => setForm({ ...form, duration: e.target.value })}
                data-testid="mt-duration"
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg" />
            </div>
          </div>
          <div>
            <label className={sectionLabel}>Class format &middot; select all that apply</label>
            <MultiSelectPills options={MT_CLASS_FORMATS} value={form.classFormat}
              onChange={(v) => setForm({ ...form, classFormat: v })} testId="mt-classformat" />
          </div>
          <div>
            <label className={sectionLabel}>Focus modes &middot; select all that apply</label>
            <MultiSelectPills options={MT_FOCUS_MODES} value={form.focusModes}
              onChange={(v) => setForm({ ...form, focusModes: v })} testId="mt-focusmode" />
          </div>
          <div>
            <label className={sectionLabel}>Coaches &middot; add one or more</label>
            <ChipInput value={form.coaches} onChange={(v) => setForm({ ...form, coaches: v })}
              placeholder="e.g. Kru Tom" suggestions={autocompleteSuggestions.coaches} testId="mt-coach" />
          </div>
        </div>

        {/* Techniques */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className={sectionHeading + ' mb-0'}>Techniques worked &middot; {form.techniques.length}</div>
            <button type="button" onClick={addTechnique} data-testid="mt-add-technique"
              className="px-3 py-1.5 bg-enf-surface-mid hover:bg-enf-accent border border-enf-surface-border hover:border-enf-accent-fg rounded-md text-xs font-semibold text-enf-text-secondary hover:text-enf-text-primary transition-colors flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Add technique
            </button>
          </div>
          {form.techniques.length === 0 ? (
            <div className="text-center py-6 text-xs text-enf-text-muted border border-dashed border-enf-surface-border rounded-lg">
              No techniques logged yet. Tap &ldquo;Add technique&rdquo; to start.
            </div>
          ) : (
            <div className="space-y-2">
              {form.techniques.map((t, i) => (
                <MTTechniqueRow key={i} value={t}
                  onChange={(next) => updateTechnique(i, next)}
                  onRemove={() => removeTechnique(i)} index={i}
                  nameSuggestions={autocompleteSuggestions.techniques} />
              ))}
            </div>
          )}
        </div>

        {/* Sparring (conditional) */}
        {showSparring && (
          <div className="mb-6 p-4 rounded-lg bg-enf-surface-mid border border-enf-surface-border" data-testid="mt-sparring-section">
            <div className={sectionHeading}>Sparring</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={sectionLabel}>Rounds</label>
                <input type="number" inputMode="numeric" min="0" value={form.sparring.rounds}
                  onChange={(e) => updSparring({ rounds: e.target.value })}
                  placeholder="0" data-testid="mt-sparring-rounds"
                  className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg" />
              </div>
              <div>
                <label className={sectionLabel}>Felt</label>
                <select value={form.sparring.feltScale}
                  onChange={(e) => updSparring({ feltScale: Number(e.target.value) })}
                  data-testid="mt-sparring-felt"
                  className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg">
                  {MT_FELT_SCALE.map((s) => <option key={s.value} value={s.value}>{s.value} &mdash; {s.label}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-3">
              <label className={sectionLabel}>Partners</label>
              <ChipInput value={form.sparring.partners}
                onChange={(v) => updSparring({ partners: v })}
                placeholder="e.g. Boon (heavyweight)"
                suggestions={autocompleteSuggestions.partners} testId="mt-sparring-partner" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <StatField label="Knockdowns landed" value={form.sparring.knockdownsLanded}
                onChange={(v) => updSparring({ knockdownsLanded: v })} testId="mt-stat-kd-landed" />
              <StatField label="Knockdowns absorbed" value={form.sparring.knockdownsAbsorbed}
                onChange={(v) => updSparring({ knockdownsAbsorbed: v })} testId="mt-stat-kd-absorbed" />
            </div>
            <div className="mt-3">
              <label className={sectionLabel}>Sparring note</label>
              <textarea value={form.sparring.note} onChange={(e) => updSparring({ note: e.target.value })}
                rows={2} placeholder="What worked, what failed, partner notes..."
                data-testid="mt-sparring-note"
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-none" />
            </div>
          </div>
        )}

        {/* Reflection */}
        <div className="mb-6 p-4 rounded-lg bg-enf-surface-mid border border-enf-surface-border" data-testid="mt-reflection-section">
          <div className={sectionHeading}>Reflection</div>
          {form.intent.trim() && (
            <div className="mb-3 p-3 rounded-md bg-enf-accent/10 border border-enf-accent-fg/30">
              <div className="text-[10px] uppercase tracking-widest text-enf-accent-fg font-semibold mb-1">Your intent today was</div>
              <div className="text-sm text-enf-text-primary italic mb-2">&ldquo;{form.intent}&rdquo;</div>
              <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-1.5">Did you accomplish it?</div>
              <div className="flex gap-1.5">
                {[{ v: true, label: 'Yes' }, { v: 'partial', label: 'Partial' }, { v: false, label: 'No' }].map((opt) => (
                  <button key={String(opt.v)} type="button" onClick={() => updReflection({ intentHit: opt.v })}
                    data-testid={`mt-intent-hit-${String(opt.v)}`} aria-pressed={form.reflection.intentHit === opt.v}
                    className={`px-3 py-1.5 text-xs rounded-md border font-semibold transition-colors ${
                      form.reflection.intentHit === opt.v
                        ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary'
                        : 'bg-enf-surface-deep border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                    }`}>{opt.label}</button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className={sectionLabel}>Biggest win</label>
              <textarea value={form.reflection.win} onChange={(e) => updReflection({ win: e.target.value })}
                rows={2} placeholder="What worked today?" data-testid="mt-reflection-win"
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-none" />
            </div>
            <div>
              <label className={sectionLabel}>Biggest gap</label>
              <textarea value={form.reflection.gap} onChange={(e) => updReflection({ gap: e.target.value })}
                rows={2} placeholder="What didn't work?" data-testid="mt-reflection-gap"
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-none" />
            </div>
            <div>
              <label className={sectionLabel}>Focus for next session</label>
              <input type="text" value={form.reflection.nextFocus}
                onChange={(e) => updReflection({ nextFocus: e.target.value })}
                placeholder="What you'll work next time" data-testid="mt-reflection-next"
                className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg" />
            </div>
          </div>
        </div>

        {/* Injury */}
        <div className="mb-6">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.injuryEnabled}
              onChange={(e) => setForm({ ...form, injuryEnabled: e.target.checked })}
              data-testid="mt-injury-toggle"
              className="w-4 h-4 rounded border-enf-surface-border bg-enf-surface-mid" />
            <span className="text-sm text-enf-text-secondary">I tweaked something today (log an injury)</span>
          </label>
          {form.injuryEnabled && (
            <div className="mt-3 p-3 rounded-md bg-enf-role-warning/5 border border-enf-role-warning/30 space-y-2">
              <div>
                <label className={sectionLabel}>Body part</label>
                <input type="text" value={form.injury.bodyPart}
                  onChange={(e) => updInjury({ bodyPart: e.target.value })}
                  placeholder="e.g. left shin, right knee" data-testid="mt-injury-bodypart"
                  className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg" />
              </div>
              <div>
                <label className={sectionLabel}>Note</label>
                <textarea value={form.injury.note} onChange={(e) => updInjury({ note: e.target.value })}
                  rows={2} placeholder="What happened, how it feels" data-testid="mt-injury-note"
                  className="w-full px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-none" />
              </div>
            </div>
          )}
        </div>

        {/* Notes + attachments */}
        <div className="mb-6 space-y-4">
          <div>
            <label className={sectionLabel}>General notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3} placeholder="Anything else worth remembering"
              data-testid="mt-general-notes"
              className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary text-sm focus:outline-none focus:border-enf-accent-fg resize-none" />
          </div>
          <div>
            <label className={sectionLabel}>Attachments ({form.attachments.length})</label>
            <div className="space-y-2">
              <MediaCaptureMenu onCapture={(payload) => addAttachment(payload)} accept="both" multiple={false}
                label="Add photo or video" testId="mt-attachment-add" recordingEtiquette
                className="w-full p-3 border border-dashed border-enf-surface-border hover:border-enf-role-info rounded-md text-sm text-enf-text-secondary hover:text-enf-role-info transition-colors flex items-center justify-center gap-2" />
              <PasteUrlInput onAdd={addExternalUrl} testId="mt-paste-url" />
            </div>
            {error && (
              <div className="text-xs text-enf-role-warning mt-2 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" /> {error}
              </div>
            )}
            {form.attachments.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                {form.attachments.map((a, i) => (
                  <AttachmentTile key={i} att={a} onRemove={() => removeAtt(i)} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button onClick={handleClose} className="flex-1 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold transition-colors" data-testid="mt-cancel">Cancel</button>
          <button onClick={handleSave} className="flex-1 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors" data-testid="mt-save">
            <Trophy className="w-4 h-4" /> Save session
          </button>
        </div>
      </div>

      {confirmDiscard && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80"
          onClick={(e) => { e.stopPropagation(); confirmDiscardNo(); }}
          data-testid="mt-confirm-discard-backdrop">
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5"
            role="dialog" aria-labelledby="mt-confirm-discard-title">
            <div id="mt-confirm-discard-title" className="font-display text-xl tracking-wide leading-none mb-2">Discard this draft?</div>
            <div className="text-sm text-enf-text-secondary mb-5">Your in-progress session will be lost.</div>
            <div className="flex gap-3">
              <button onClick={confirmDiscardNo} data-testid="mt-confirm-discard-no"
                className="flex-1 py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold transition-colors">Keep editing</button>
              <button onClick={confirmDiscardYes} data-testid="mt-confirm-discard-yes"
                className="flex-1 py-2 bg-enf-role-warning/15 hover:bg-enf-role-warning/25 border border-enf-role-warning/50 text-enf-role-warning rounded-md text-sm font-semibold transition-colors">Discard</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatField = ({ label, value, onChange, testId }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1 block">{label}</label>
    <input type="number" inputMode="numeric" min="0" value={value}
      onChange={(e) => onChange(e.target.value)} placeholder="0" data-testid={testId}
      className="w-full px-3 py-1.5 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm text-center focus:outline-none focus:border-enf-accent-fg" />
  </div>
);

export default MuayThaiLogDialog;
